import moment from "moment";

// First touch marketing channel of one person, plus their booking history.
// The server sends the raw referrer fields from person_referrer_event_report,
// so this is missing for people who signed up before referrer tracking.
//
// Call add_acquisition_observables(self, data.acquisition) in a class
// constructor. It adds the acquisition observable and every acquisition_*
// computed, so the income table, the invite list and the members list show
// the same icons, the same labels and the same modal fields.
//
// map_acquisition_channel turns the raw fields into a channel here, not in
// SQL, so every list maps the same way from one place.

const ACQUISITION_ICONS = {
  google_ads: "mdi-google-ads text-danger",
  google_organic: "mdi-google text-success",
  meta_ads: "mdi-facebook text-danger",
  meta_organic: "mdi-facebook text-success",
  pinterest: "mdi-pinterest text-danger",
  other_search: "mdi-magnify text-success",
  artist_widget: "mdi-account-music text-primary",
  ai_search: "mdi-robot text-info",
  direct: "mdi-cursor-default-click text-muted",
  other: "mdi-web text-muted",
};

const ACQUISITION_LABELS = {
  google_ads: "Google Ads",
  google_organic: "Google Organic",
  meta_ads: "Meta Ads",
  meta_organic: "Meta Organic",
  pinterest: "Pinterest",
  other_search: "Other Search",
  artist_widget: "Artist Widget",
  ai_search: "AI Search",
  direct: "Direct / Unknown",
  other: "Other Source",
};

// First touch channel from the raw referrer fields. Returns null when there
// is no person_referrer_event_report row, so the label falls back to
// "No Referrer". Keep this in step with the ad-report and acquisition funnel.
export function map_acquisition_channel(a) {
  if (!a || a.referrer_group == null) {
    return null;
  }

  const lp = (a.landing_page || "").toLowerCase();
  const ref = (a.referrer || "").toLowerCase();
  const group = a.referrer_group;

  if (
    lp.includes("gclid=") ||
    lp.includes("gbraid=") ||
    lp.includes("wbraid=") ||
    lp.includes("c_id=") ||
    lp.includes("utm_source=adwords")
  ) {
    return "google_ads";
  }

  if (
    lp.includes("fbclid=") ||
    lp.includes("utm_source=meta") ||
    lp.includes("utm_source=fb") ||
    lp.includes("utm_source=ig")
  ) {
    return "meta_ads";
  }

  if (
    ref.includes("pinterest") ||
    lp.includes("epik=") ||
    lp.includes("utm_source=pinterest")
  ) {
    return "pinterest";
  }

  if (group === "google") return "google_organic";
  if (group === "facebook" || group === "instagram") return "meta_organic";
  if (["bing", "ecosia", "duckduckgo", "yahoo"].includes(group)) {
    return "other_search";
  }
  if (group === "artist_page") return "artist_widget";

  if (
    lp.includes("utm_source=chatgpt") ||
    ref.includes("chatgpt") ||
    ref.includes("perplexity")
  ) {
    return "ai_search";
  }

  if (group === "unknown") return "direct";
  return "other";
}

export function add_acquisition_observables(self, acquisition) {
  self.acquisition = ko.observable(acquisition || null);

  self.acquisition_channel = ko.pureComputed(function () {
    return map_acquisition_channel(self.acquisition());
  });

  // No acquisition at all means we could not name the host. That happens on
  // a request with no host row yet, where the creator is the artist.
  self.acquisition_icon = ko.pureComputed(function () {
    const a = self.acquisition();
    if (!a) {
      return "mdi-account-question text-muted";
    }
    return (
      ACQUISITION_ICONS[self.acquisition_channel()] ||
      "mdi-help-circle-outline text-muted"
    );
  });

  self.acquisition_label = ko.pureComputed(function () {
    const a = self.acquisition();
    if (!a) {
      return "No Host Found";
    }
    return ACQUISITION_LABELS[self.acquisition_channel()] || "No Referrer";
  });

  self.acquisition_referrer = ko.pureComputed(function () {
    return self.acquisition()?.referrer || "No Referrer";
  });

  self.acquisition_landing_page = ko.pureComputed(function () {
    return self.acquisition()?.landing_page || "No Landing Page";
  });

  self.acquisition_campaign_id = ko.pureComputed(function () {
    return self.acquisition()?.campaign_id || "No Campaign";
  });

  self.acquisition_email = ko.pureComputed(function () {
    return self.acquisition()?.email_address || "No Email";
  });

  self.acquisition_signed_up = ko.pureComputed(function () {
    const signed_up = self.acquisition()?.signed_up;
    return signed_up ? new moment(signed_up).format("DD.MM.YYYY") : "Unknown";
  });

  self.acquisition_years = ko.pureComputed(function () {
    const signed_up = self.acquisition()?.signed_up;
    return signed_up ? new moment().diff(new moment(signed_up), "years") : 0;
  });

  // One star for every full two years on the platform.
  self.acquisition_stars = ko.pureComputed(function () {
    return new Array(Math.floor(self.acquisition_years() / 2)).fill(0);
  });

  self.acquisition_confirmed_events = ko.pureComputed(function () {
    return self.acquisition()?.confirmed_events || 0;
  });

  // More than one confirmed concert, so this host came back.
  self.acquisition_repeat_booker = ko.pureComputed(function () {
    return self.acquisition()?.repeat_booker === true;
  });
}
